import * as ActionTypes from 'actions/ActionTypes'
import * as ActionsLayer from 'actions/layer'
import { cloneDeep, groupBy, isArray, isEmpty, isNil } from 'lodash'
import { PlaceholderAsset, TransitionAsset } from 'models/Asset'
import Layer from '~/models/Layer'
import * as Selectors from '~/selectors'
import { parseLoadedFile } from '~/Util/sourceFile'
import { createAssetFromSourceFile, createGroupAssetsFromSourceFiles } from './createAssetFromSourceFile'
import { insertLayer } from './insertLayer'

import { patchDnDItemAssets } from '~/components/Timeline/DragAndDrop/lib/patchDnDAssets'
import { TRANSITION_MOUNT_TYPE, PLAYBACK_STATE } from '~/enums'
import { addAttachedTransition } from '~/helpers/actions/addAttachedTransition'
import { addRelatedAssets } from '~/helpers/actions/addRelatedAssets'
import { closeEditingFormOfDeletion } from '~/helpers/actions/closeEditingFormOfDeletion'
import { getTransitionStartTime } from '~/helpers/actions/getTransitionStartTime'
import { normalizeAssetStartTime } from '~/helpers/actions/normalizeAssetStartTime'
import { splitAssetsByType } from '~/helpers/actions/splitAssetsByType'
import { isAvailableAttachTransition } from '~/helpers/assets/isAvailableAttachTransition'
import { time2Pixel, secondsToTimelineTime, DEFAULT_VIDEO_HEIGHT,
  getWindowWidth, pixel2Time, DEFAULT_FPS, DEFAULT_VIDEO_WIDTH } from '~/Util'
import { DROP_STATUS, getDropStatusPosition } from './dragActionHelpers/getDropStatusPosition'
import { getInitialMoveMultipleAssetsData } from './dragActionHelpers/getInitialMoveMultipleAssetsData'
import { addNewLayers } from './dragActionIntermediaries/addNewLayers'
import { changeLayers } from './dragActionIntermediaries/changeLayers'
import { updateAssetsPosition } from './dragActionIntermediaries/updateAssetsPosition'
import { updateLayerIndexes } from './dragActionHelpers/updateLayerIndexes'
import { openAssetSettingForm, showReferenceFileChoiceAction } from './mainView'
import { selectInOutPointsTimeLineAssetID, selectorPreviewClipCreator } from '~/selectors/preview'
import { getAssetMediaThumbnailFromBackend } from '~/helpers/assets/getAssetMediaThumbnailFromBackend'
import { rebuildAssets } from './projectData/mappings/getAssetMapping'
import {
  LEFT_MENU_WIDTH,
  MEDIA_BLOCK_MIN_WIDTH,
  LAYER_CONTROL_WIDTH,
  STORAGE_CONST,
  REWIND_SOURCES
} from '~/constant'
import { selectElementDisplayType, selectSplitSize } from '~/selectors/useInterface'
import { DISPLAY_TYPE } from '~/config/constants/ui'
import { isVideo } from '~/Util/assets'
import { selectBehaviourSettings } from '~/selectors/mainView'
import { TIME_CODE_CONTAINER_LOCATION, TIME_CODE_MODE } from '~/config/constants/timecode'
import { changeTimelinePlaybackState, setTimeCodeMode } from './playback'
import { BehaviourSettings } from '~/config/constants/editorSettings'
import { getOutputFormat } from '~/Util/project'

export const setIsSliderOnBufferingPos = payload => ({
  type: ActionTypes.SET_IS_SLIDER_ON_BUFFERING_POS,
  payload,
})

export const setTimelineAssetIds = payload => ({
  type: ActionTypes.SET_TIMELINE_ASSET_IDS,
  payload,
})

export const setIsLoadingTimeline = isLoading => ({
  type: ActionTypes.SET_IS_LOADING_TIMELINE,
  payload: { isLoading: Boolean(isLoading) },
})

export const updateSlider = time => ({
  type: ActionTypes.UPDATE_SLIDER,
  payload: { time },
})

export const updatePlayingAssets = time => ({
  type: ActionTypes.UPDATE_PLAYING_ASSETS,
  payload: { time },
})

/**
 * Creates an action to rewind the timeline.
 * @param {number} time - The target time in seconds to seek to.
 * @param {string} [source=REWIND_SOURCES.DEFAULT] - The origin of the call.
 * Allows the reducer and sagas to distinguish automatic rewinding from manual dragging of the slider or call context
 */
export const rewind = (time, source = REWIND_SOURCES.DEFAULT) => ({
  type: ActionTypes.TIMELINE_REWINDED,
  payload: { time, source },
})

export const clearTimeline = () => async dispatch => dispatch({
  type: ActionTypes.TIMELINE_CLEARED,
})

export const setScrollLeft = scrollLeft => ({
  type: ActionTypes.TIMELINE_SCROLL_LEFT,
  payload: { scrollLeft },
})

export const setScale = scale => ({
  type: ActionTypes.SET_TIMELINE_SCALE,
  payload: { scale },
})

export const setMaxScale = scale => ({
  type: ActionTypes.SET_MAX_TIMELINE_SCALE,
  payload: { scale },
})

export const setMinScale = scale => ({
  type: ActionTypes.SET_MIN_TIMELINE_SCALE,
  payload: { scale },
})

export const setReference = reference => ({
  type: ActionTypes.SET_REFERENCE,
  payload: reference,
})

export const setInitialReference = file => (dispatch, getState) => {
  const state = getState()
  const refFile = Selectors.getReferenceVideoAsset(state)
  const behaviourSettings = selectBehaviourSettings(state)
  const allAssets = Selectors.getAssets(state)
  // file - is media file
  const { width, height, fpsNum, fpsDenum, id, filetype: fileType } = file
  const noRefParams = isNil(refFile.width) && isNil(refFile.height)

  if (behaviourSettings[
    BehaviourSettings.setTimecodeFormatBasedOnMedia
  ] && isEmpty(allAssets)) {
    const mode = file.dfFlag ? TIME_CODE_MODE.FRAME_DF : TIME_CODE_MODE.FRAME_NDF
    dispatch(setTimeCodeMode({
      mode,
      containerLocation: TIME_CODE_CONTAINER_LOCATION.TIMELINE,
    }))
  }

  if (noRefParams) {
    // A 90/270 degrees rotated media file has swapped display dims, so the
    // reference/canvas frame must match the actual rendered orientation.
    let referenceWidth = isVideo(file) ? width : DEFAULT_VIDEO_WIDTH
    let referenceHeight = isVideo(file) ? height : DEFAULT_VIDEO_HEIGHT
    if (isVideo(file) && [ 90, 270 ].includes(file.rotationDegrees)) {
      referenceWidth = height
      referenceHeight = width
    }
    dispatch(setReference({
      width: referenceWidth,
      height: referenceHeight,
      fpsNum: isVideo(file) ? fpsNum : DEFAULT_FPS,
      fpsDenum: isVideo(file) ? fpsDenum : 1,
      id: isVideo(file) ? id : null,
      fileType: getOutputFormat({ fileType }),
    }))
  }
}

export const setSliderTimeCenter = (
  sliderProgress, snapCenterRuller = false
) => (dispatch, getState) => {
  const state = getState()
  const { scale, scrollLeft } = state.timeline
  const {
    timeline: timelineDisplayType,
  } = selectElementDisplayType(state)
  const windowWidth = getWindowWidth()
  const { media: mediaColumnSize } = selectSplitSize(state)
  const offset = timelineDisplayType === DISPLAY_TYPE.FULL_WIDTH
    ? (windowWidth / 2) - LEFT_MENU_WIDTH
    : ((windowWidth - (mediaColumnSize
      || (MEDIA_BLOCK_MIN_WIDTH + LEFT_MENU_WIDTH))) / 2) - LEFT_MENU_WIDTH

  const endTimelineOffset = timelineDisplayType === DISPLAY_TYPE.FULL_WIDTH
    ? 0
    : mediaColumnSize

  const startTimelineOffset = snapCenterRuller ? 0 : scrollLeft - LAYER_CONTROL_WIDTH
  const endTimeLineValue = pixel2Time(windowWidth + startTimelineOffset - endTimelineOffset, scale)

  const startTimeValue = pixel2Time(scrollLeft, scale)

  const newPosition = time2Pixel(sliderProgress, scale) - offset
  if (snapCenterRuller) {
    dispatch(setScrollLeft(newPosition >= 0 ? newPosition : 0))
  } else if (sliderProgress > endTimeLineValue || sliderProgress < startTimeValue) {
    dispatch(setScrollLeft(newPosition))
  } else if (newPosition <= 0 && scrollLeft !== 0) {
    dispatch(setScrollLeft(0))
  }
}

export const setIsMovingSlider = isMovingSlider => ({
  type: ActionTypes.SET_IS_MOVING_SLIDER,
  payload: { isMovingSlider },
})

/**
 * @param {number} index
 * @param {TimelineLayer[]} [layers]
 */
export const insertLayers = (
  index, layers, { insertBefore = true, addToHistory = true }
) => ({
  type: ActionTypes.INSERT_LAYERS,
  payload: { index, layers, insertBefore, addToHistory },
})

export const muteLayer = (id, mute) => ({
  type: ActionTypes.LAYER_MUTED,
  payload: { id, mute },
})

export const toggleVisibleLayer = layerId => ({
  type: ActionTypes.LAYER_TOGGLE_VISIBLE,
  payload: { layerId },
})

export const deleteTempAssets = ids => ({
  type: ActionTypes.DELETE_TEMP_ASSETS,
  payload: ids,
})

export const forceDeleteEmptyLayer = ids => ({
  type: ActionTypes.FORCE_DELETE_LAYER,
  payload: { ids },
})

export const setScrollX = (sliderTime,
  scale, offset = 0) => setScrollLeft(time2Pixel(sliderTime, scale) - offset)


export const onScrollX = (newSliderTime, offset = 0) => async (dispatch, getState) => {
  const { scale, sliderTime } = getState().timeline
  dispatch(setScrollX(newSliderTime ?? sliderTime, scale, offset))
}

export const changeLayerPosition = (id, index) => (dispatch, getState) => {
  const prevIndex = Selectors.getLayers(getState()).findIndex(l => l.id === id)
  dispatch({
    type: ActionTypes.LAYER_CHANGE_POSITION,
    payload: { id, index, prevIndex },
  })
}


export const deleteTransitions = transitions => (dispatch, getState) => {
  closeEditingFormOfDeletion(dispatch, getState(), transitions)
  dispatch({ type: ActionTypes.DELETE_TRANSITIONS,
    payload: { transitions } })
}

export const deletePlaceholders = (placeholders, options) => dispatch => {
  dispatch({ type: ActionTypes.DELETE_PLACEHOLDERS, payload: { placeholders, options } })
}

/**
 * @param {Asset|Asset[]} assets
 */
export const deleteAssets = (assets, prevCopyAssetIds = []) => async (dispatch, getState) => {
  const state = getState()
  const allAssets = Selectors.getAssets(state)
  const assetIds = assets.map(a => a.id)
  const assetsToRemove = allAssets.filter(a => assetIds.includes(a.id))
  const layers = Selectors.getLayers(state)
  const transitionsToRemove = [].concat(assetsToRemove)
    .filter(asset => asset instanceof TransitionAsset)
  let {
    placeholders,
    transitions,
    others,
  } = splitAssetsByType(allAssets, assetsToRemove, layers)
  if (isEmpty(transitionsToRemove)) {
    // NOTE: if an asset is deleted along with an attached transition
    transitions = addAttachedTransition(transitions, allAssets, others)
  }
  const relatedAssets = addRelatedAssets(!isEmpty(transitionsToRemove)
    ? transitionsToRemove : transitions, allAssets, others)
  // NOTE: order is important
  // NOTE: empty arrays is important (for undo-redo)
  await dispatch(deletePlaceholders(placeholders))
  await dispatch(deleteTransitions(transitions))

  closeEditingFormOfDeletion(dispatch, getState(), others)

  await dispatch({
    type: ActionTypes.DELETE_ASSETS,
    payload: {
      relatedAssets,
      assets: others,
      transitions,
      prevCopyAssetIds,
    },
  })
}

export const rippleDelete = assets => async (dispatch, getState) => {
  const state = getState()
  const allAssets = Selectors.getAssets(state)
  const assetIds = assets?.map(a => a.id)
  const assetsToRemove = allAssets.filter(a => assets ? assetIds.includes(a.id) : a.selected)
  let {
    placeholders = [],
    transitions = [],
    others = [],
  } = groupBy(
    assetsToRemove.filter(a => !(a instanceof TransitionAsset)),
    asset => {
      if (asset instanceof PlaceholderAsset) {
        return 'placeholders'
      }
      return 'others'
    }
  )

  if (others.length || placeholders.length) {
    const actionAssets = others.concat(placeholders)
    const firstAsset = actionAssets.sort((a, b) => a.startTime - b.startTime)[0]
    const patchedAssets = state.layer.assets.filter(a => !actionAssets.some(aa => aa.id === a.id)
      && a.startTime > firstAsset.startTime)
    transitions = Selectors.getTransitionAssets(state)
      .filter(t => others.some(a => t.isAttachedTo(a.id)))

    await dispatch(ActionsLayer.clearAssetsSelection())
    await dispatch(deleteTransitions(transitions))

    closeEditingFormOfDeletion(dispatch, state, others)

    await dispatch({
      type: ActionTypes.RIPPLE_DELETE_ASSETS,
      payload: {
        assets: others,
        transitions,
        placeholders,
        patchedAssets,
      },
    })
  }
}

export const deleteLayer = (id, isHovering = false,
  isTransit = false, recordInHistory = true) => async (
  dispatch, getState
) => {
  const layers = Selectors.getLayers(getState())
  const layer = layers.find(l => l.id === id)
  const index = layers.findIndex(l => l.id === id)
  const layerAssets = Selectors.getLayerAssets(getState(), id)
  if (!isHovering || !layerAssets.length) {
    // NOTE: Order is imiportant for the history feature
    await dispatch({
      type: ActionTypes.LAYER_REMOVED,
      payload: { ids: [].concat(id), layer, index, isHovering, isTransit, recordInHistory },
    })
    if (layerAssets.length) {
      await dispatch(deleteAssets(layerAssets))
    }
  }
}

export const deleteLayers = ids => async (
  dispatch, getState) => {
  const layerAssets = Selectors.getLayerAssets(getState(), ids)
  if (layerAssets.length) {
    if (layerAssets.length) {
      await dispatch(deleteAssets(layerAssets))
    }
    await dispatch({
      type: ActionTypes.LAYER_REMOVED,
      payload: { ids },
    })
  }
}

export const deleteEmptyLayers = () => (dispatch, getState) => {
  const state = getState()
  const { layers } = state.timeline
  const assetsByLayers = Selectors.getAssetsByLayers(state)
  const lowerLayer = layers.at(-1)
  const ids = []
  for (const layer of layers.values()) {
    const assets = assetsByLayers.get(layer.id)
    if (assets.length === 0) {
      if (layer.id !== lowerLayer.id) {
        ids.push(layer.id)
      }
    }
  }
  dispatch(forceDeleteEmptyLayer(ids))
}

export const deleteSelectedAssets = () => async (dispatch, getState) => {
  const { assets } = getState().layer
  const selectedAssets = assets.reduce((result, el) => {
    if (el.selected) {
      result.push(el)
      /* const isDissolveTranzitionAsset = el.rightVideoAssetId && el.rightVideoAssetId
      if (isDissolveTranzitionAsset) {
        const rightAsset = assets.find(asset => asset.id === el.rightVideoAssetId)
        const leftAsset = assets.find(asset => asset.id === el.leftVideoAssetId)
        if (rightAsset) result.push(rightAsset)
        if (leftAsset) result.push(leftAsset)
      } */
    }

    return result
  }, [])
  if (selectedAssets.length > 0) {
    await dispatch(deleteAssets(selectedAssets))
  }
}

const removeTimelineIOPointsFragment = async (dispatch, getState,
  { markIn, markOut, shiftAssets }) => {
  const isRippleDelete = shiftAssets
  const state = getState()
  const { timeline } = selectorPreviewClipCreator(state)
  const layersMap = state.timeline.layers.reduce((result, layer, index) => {
    // eslint-disable-next-line no-param-reassign
    result[layer.id] = index
    return result
  }, {})
  const markInTimeline = markIn ?? (
    isNil(timeline.markIn) ? null : secondsToTimelineTime(timeline.markIn)
  )
  const markOutTimeline = markOut ?? (
    isNil(timeline.markOut) ? null : secondsToTimelineTime(timeline.markOut)
  )
  if (markInTimeline || markOutTimeline) {
    const { assets } = state.layer
    const prevExistsAssetIds = assets.map(a => a.id)
    const allFragmentAssets = assets.filter(asset => (
      !(asset.startTime > markOutTimeline) || !(asset.endTime < markInTimeline)
    ))

    const {
      deleteAssets,
      adjustableAssetDataObjects,
      layerIdsOfDeleteAssets,
    } = allFragmentAssets.reduce((result, asset) => {
      const hasTransitionTwoVideoAssets = asset.rightVideoAssetId && asset.leftVideoAssetId
      if ((asset.startTime >= markInTimeline && asset.endTime <= markOutTimeline)
        || (asset.startTime >= markInTimeline && !markOutTimeline)
        || (asset.endTime <= markOutTimeline && !markInTimeline)) {
        result.deleteAssets.push(asset)
        // eslint-disable-next-line no-param-reassign
        result.layerIdsOfDeleteAssets[
          asset.layerId
        ] = Object.keys(layersMap).indexOf(asset.layerId)
      } else if (hasTransitionTwoVideoAssets) {
        result.deleteAssets.push(asset)
      } else {
        result.adjustableAssetDataObjects.push(({
          id: asset.id,
          startTime: asset.startTime,
          duration: asset.duration,
          mediaStart: asset.mediaStart,
          maxDuration: asset.maxDuration,
          mediaFileDuration: asset.mediaFileDuration,
        }))
      }
      return result
    }, {
      deleteAssets: [],
      layerIdsOfDeleteAssets: {},
      adjustableAssetDataObjects: [],
    })

    await dispatch({
      type: ActionTypes.DELETE_IO_POINTS_TIMELINE_FRAGMENT,
      payload: {
        deleteAssets,
        adjustableAssetDataObjects,
        markInTimeline,
        markOutTimeline,
        layerIdsOfDeleteAssets,
        layersMap,
        recordInHistory: !isRippleDelete,
        prevExistsAssetIds,
      },
    })

    // Checking layers which have any assets
    const layersWithAssets = getState().layer.assets.map(asset => asset.layerId)
    const lastLayerIndex = Object.keys(layersMap).length - 1
    const deleteLayersIds = Object.keys(layersMap).filter((layerId, index) => (
      !layersWithAssets.includes(layerId) && (index !== lastLayerIndex)
    ))

    dispatch({
      type: ActionTypes.LAYER_REMOVED,
      payload: { ids: deleteLayersIds, recordInHistory: false },
    })

    if (isRippleDelete) {
      const shiftAssets = getState().layer
        .assets
        .filter(asset => asset.startTime >= markOutTimeline)
      const deltaShiftTime = markOutTimeline ? markOutTimeline - (markInTimeline ?? 0) : 0
      dispatch({
        type: ActionTypes.RIPPLE_DELETE_IO_POINTS_TIMELINE_FRAGMENT,
        payload: {
          deleteAssets,
          adjustableAssetDataObjects,
          markInTimeline,
          markOutTimeline,
          layerIdsOfDeleteAssets,
          layersMap,
          recordInHistory: isRippleDelete,
          shiftAssetIds: shiftAssets.map(asset => asset.id),
          deltaShiftTime,
          prevExistsAssetIds,
        },
      })
    }
  }
}

// Props markIn, markOut by *redo() from DeleteTimelineFragmentAction
export const deleteTimelineFragment = params => (dispatch, getState) => {
  const { markIn, markOut } = params ?? {}
  removeTimelineIOPointsFragment(dispatch, getState, { markIn, markOut, shiftAssets: false })
}

export const rippleDeleteTimelineFragment = params => (dispatch, getState) => {
  const { markIn, markOut } = params ?? {}
  removeTimelineIOPointsFragment(dispatch, getState, { markIn, markOut, shiftAssets: true })
}

export const undoDeleteTimelineFragment = ({
  adjustableAssetDataObjects,
  deleteAssets,
  layerIdsOfDeleteAssets,
  layersMap,
  shiftAssetIds,
  deltaShiftTime,
  prevExistsAssetIds,
}) => async (dispatch, getState) => {
  const state = getState()
  const withThumbnail = await Promise.all(deleteAssets.map(async objectAsset => {
    const thumbnail = await getAssetMediaThumbnailFromBackend(objectAsset)
    const cloneObjectAsset = cloneDeep(objectAsset)
    cloneObjectAsset.thumbnail = thumbnail
    return cloneObjectAsset
  }))

  const existsLayerIds = state.timeline.layers.map(l => l.id)
  const deletedLayerIds = Object.keys(layerIdsOfDeleteAssets).filter(layerId => (
    !existsLayerIds.includes(layerId)
  ))

  dispatch({
    type: ActionTypes.UNDO_DELETE_IO_POINTS_TIMELINE_FRAGMENT,
    payload: {
      adjustableAssetDataObjects,
      deleteAssets: rebuildAssets(withThumbnail),
      deletedLayerIds,
      layersMap,
      shiftAssetIds,
      deltaShiftTime,
      prevExistsAssetIds,
    },
  })
}

export const setDuration = duration => ({
  type: ActionTypes.SET_TIMELINE_DURATION,
  payload: { duration },
})

export const setRullerOffset = offset => ({
  type: ActionTypes.SET_TIMELINE_RULLER_OFFSET,
  payload: { offset },
})

export const rebuildTimeLineRuller = () => (dispatch, getState) => {
  const state = getState()
  const { sliderTime } = state.timeline
  dispatch(setSliderTimeCenter(sliderTime))
}

export const hoverAssetByLayerIndex = (hoveredLayerIndex, hoveringItem) => async dispatch => {
  // @link https://docs.google.com/document/d/1vsdg-XX2805oFctMGgXkvJPQAes0kQJLksRKMBejKHg/edit?ts=5e997f22#bookmark=id.12ypdqbtsajx
  // If dragged clip hovers an asset, and layer above is contais asset at future hovering position,
  // create new empty layer for dragged clip personally
  if (hoveredLayerIndex === 0 || hoveringItem.intersectedPxls !== 0) {
    await dispatch(insertLayer({ index: hoveredLayerIndex, layer: new Layer(), isHovering: true }))
  }
}

export const focusTimelineRegion = () => dispatch => {
  dispatch({
    type: ActionTypes.TIMELINE_RECEIVED_FOCUS,
  })
}

export const startDraggingItemOverTimeline = () => dispatch => {
  // Drag clip over timeline should be considered as focusing timeline block
  // @link http://18.184.210.86/issues/335
  dispatch(focusTimelineRegion())

  dispatch({
    type: ActionTypes.DRAG_OVER_TIMELINE_STARTED,
    payload: undefined,
  })
}

// Only for startRecording (voiceover)
export const addAssetFromFileAtSliderPosition = file => (dispatch, getState) => {
  const state = getState()
  const { sliderTime, layers } = state.timeline

  const targetLayerIndex = 0
  let targetLayer = layers[targetLayerIndex]
  const rightSideAssetsByLayer = layers.map(layer => Selectors
    .getRightSideAssetsBySliderTimeAndLayerByEndTime(state, layer.id))
  const layerIndex = rightSideAssetsByLayer.findIndex(assets => assets.length === 0)
  targetLayer = layerIndex !== -1 ? layers[layerIndex] : null

  let assetLayerId
  if (targetLayer) {
    assetLayerId = targetLayer.id
  } else {
    assetLayerId = layers[layers.length - 1].id
  }
  const referenceVideo = Selectors.getReferenceVideoAsset(state)

  const asset = createAssetFromSourceFile(file, {
    layerId: assetLayerId,
    startTime: sliderTime,
    pregeneratedId: file.id,
    videoSize: { width: referenceVideo.width, height: referenceVideo.height },
  })

  dispatch(ActionsLayer.addTempAssetToTimeline([ asset ]))
}

export const addAssetFromFile = ({
  file = null,
  layerId,
  newLayerIds = [],
  startTime,
  pregeneratedId,
  aspectRatio = undefined,
  ignoreSliderTimer = false,
  isTransit = false,
  deltaOnDrop,
  selected = true,
  isSourceFile = false, // redo operation used exists assets
}) => (dispatch, getState) => {
  let pendingAssets = isArray(file) ? file : [ file ]
  const state = getState()
  const referenceVideo = Selectors.getReferenceVideoAsset(state)
  const layers = Selectors.getLayers(state)
  const layerIds = layers.map(l => l.id)

  if (file === null && pregeneratedId !== null) {
    pendingAssets = Selectors.getSourceFilesByIds(state, [].concat(pregeneratedId))
  }

  const isExistsLayers = [].concat(layerId).every(id => layerIds.includes(id))
  let layerForAddingAssetsId = isSourceFile ? [].concat(layerId)[0] : null

  if (!isExistsLayers) {
    layerForAddingAssetsId = layers.at(-1)?.id
    const layerAssets = Selectors.getLayerAssets(getState(), layerForAddingAssetsId)
    if (layerAssets.length !== 0) {
      dispatch(insertLayer({ index: layers.length, isAdditional: true }))
      layerForAddingAssetsId = Selectors.getLayers(getState()).at(-1)?.id
    }
  }

  const inOutPointsTimeLineAssetId = selectInOutPointsTimeLineAssetID(state)
  const inOutPointsTimeLineAsset = Selectors.selectAssetById(state, inOutPointsTimeLineAssetId)

  pendingAssets = pendingAssets
    .map(f => f.id)
    .includes(inOutPointsTimeLineAsset?.fileId)
    ? [ inOutPointsTimeLineAsset ]
    : pendingAssets

  const pendingAssetsDuration = pendingAssets.reduce((result, file) => result + file.duration, 0)

  const startTimeByAssetId = pendingAssets.reduce((result, asset) => {
    // eslint-disable-next-line no-param-reassign
    result[asset.id] = normalizeAssetStartTime(state, Selectors.getLayerAssets, {
      startTime: asset.startTime,
      // asset.layerId - for redo (AssetCreation) or loadProject operation with exists assets. pendingAssetsDuration for source media
      duration: isSourceFile ? pendingAssetsDuration : asset.duration,
      layerId: isSourceFile ? layerForAddingAssetsId : asset.layerId,
      ignoreStartOverlapping: ignoreSliderTimer,
    })
    return result
  }, {})

  const creationOrderByLayerId = [].concat(layerId).reduce((result, id) => {
    // eslint-disable-next-line no-param-reassign
    result[id] = Selectors.getLayerAssets(state, id).length
    return result
  }, {})
  const asset = createGroupAssetsFromSourceFiles(pendingAssets, {
    layerId: layerForAddingAssetsId,
    startTime,
    startTimeByAssetId,
    pregeneratedIds: [].concat(pregeneratedId),
    creationOrderByLayerId,
    videoSize: { width: referenceVideo.width, height: referenceVideo.height },
    aspectRatio,
    isSourceFile,
  })

  const newLayerData = {
    asset, ignoreSliderTimer, isTransit, deltaOnDrop, selected, newLayerIds,
  }

  dispatch(ActionsLayer.addAsset(newLayerData))

  // return asset.id
}

export const dropSourceFile = (id,
  {
    layerId: baseLayerId,
    dragItem,
    layerIndex,
    isNewLayerAction,
    startTime,
    pregeneratedId,
    isTransit = false,
    deltaOnDrop,
  }) => (dispatch, getState) => {
  const state = getState()
  const selectedSourceFileIds = dragItem.selectedItems?.map(fileItem => fileItem.id) ?? []
  const fileIds = id ? [].concat(id) : selectedSourceFileIds
  const file = Selectors.getSourceFilesByIds(state, fileIds)
  const isDifferentFileParams = Selectors.isDifferentFileRefParamsSelector(state, fileIds)
  const layers = Selectors.getLayers(state)
  const layerId = baseLayerId || layers[layerIndex].id
  const allAssets = Selectors.getAssets(state)
  const { id: referenceId } = Selectors.getReferenceVideoAsset(state)

  const action = addAssetFromFile({
    file,
    layerId,
    startTime,
    pregeneratedId,
    isTransit,
    deltaOnDrop,
    isSourceFile: true,
    newLayerIds: isNewLayerAction || layers.length - 1 === layerIndex ? [ layerId ] : [],
  })

  if (file.length === 1) {
    dispatch(setInitialReference(file[0]))
    dispatch(action)
  } else if (isDifferentFileParams && file.length > 1 && (!referenceId || isEmpty(allAssets))) {
    dispatch(showReferenceFileChoiceAction({
      visibility: true,
      getAction: () => action,
    }))
  } else {
    dispatch(action)
  }
}

/**
 *
 * @param {ProjectData.ProjectAsset} asset
 * @param {{layerId: string, startTime: number, pregeneratedId: string, isSourceFile: boolean}} param1
 */
export const loadProjectAsset = (projectAsset,
  { layerId, startTime, pregeneratedId, selected, isSourceFile }) => dispatch => {
  const file = parseLoadedFile(projectAsset.sourceFileType, projectAsset)
  const { aspectRatio } = projectAsset?.settings || {}
  dispatch(addAssetFromFile({
    file,
    layerId,
    startTime,
    pregeneratedId,
    aspectRatio,
    ignoreSliderTimer: true,
    selected,
    isSourceFile,
  }))
}

export const moveAsset = (
  assetId, { startTime, layerId, layerIndex, isTransit = false, deltaOnDrop }
) => async (dispatch, getState) => {
  let transitions = []
  const state = getState()
  const layers = Selectors.getLayers(state)
  const asset = Selectors.selectAssetById(state, assetId)
  const targetLayerId = layerId || layers[layerIndex]?.id
  // NOTE: order of dispatches is important for undo-redo handling
  // if video asset is moved on timeline, remove related transitions except fade in/out
  if (asset.canHaveTransition) {
    const [ , transitionsToRemove ] = Selectors.getTransitionsOfMovedAsset(state.layer, asset.id)
    transitions = transitionsToRemove
    // NOTE: for undo-redo also important sending empty array of transitionsToRemove
    await dispatch(deleteTransitions(transitionsToRemove))
  }

  await dispatch({
    type: ActionTypes.ASSET_MOVED_ON_TIMELINE,
    payload: {
      asset,
      layerId: targetLayerId,
      startTime: normalizeAssetStartTime(getState(), Selectors.getLayerAssets, {
        assetId,
        startTime,
        duration: asset.duration,
        layerId: targetLayerId,
      }),
      isTransit,
      deltaOnDrop,
      transitions,
    },
  })
  // await dispatch(ActionsLayer.shiftItemsByAsset(asset))
}

export const moveMultipleAssets = (
  assetId, {
    startTime,
    layerId,
    layerIndex,
    isTransit = false,
    deltaOnDrop,
    dragItem,
    isNewLayerAction,
    dropOffsetX,
    draggableBlockStartTime,
    isIntersectionPlaceholder,
    intersectionPlaceholder,
  }
) => async (dispatch, getState) => {
  const state = getState()
  const { dndPatchAssets } = state.layer
  // If store was patched after being (callback begin() from useDragSource) dnd
  const patchedDragItem = patchDnDItemAssets(dragItem, dndPatchAssets)

  const {
    currentDragItem,
    dragAssetIds,
    dragAssetsByLayerIndex,
    targetLayerId,
    countDragAssetsLayers,
    currentDragItemLayerId,
    currentDragItemLayerIndex,
    targetLayerIndex,
    staticAssets,
    layers,
    layerIds,
    firstdragAssetStartTime,
  } = getInitialMoveMultipleAssetsData({
    state,
    assetId,
    dragItem: patchedDragItem,
    layerId,
    layerIndex,
  })

  const serviceData = {
    assets: state.layer.assets.map(asset => (
      {
        id: asset.id,
        layerId: asset.layerId,
        startTime: asset.startTime,
      }
    )),
    insertBetweenLayerID: intersectionPlaceholder?.layerId,
    selectedDragIds: patchedDragItem.selectedDragIds,
    layerIds: Array.from(new Set(layerIds)),
  }

  const redoData = {
    assetId,
    startTime,
    layerId,
    layerIndex,
    isTransit,
    deltaOnDrop,
    dragItem: patchedDragItem,
    isNewLayerAction,
    dropOffsetX,
    draggableBlockStartTime,
    isIntersectionPlaceholder,
  }

  const dropStatus = getDropStatusPosition({
    currentDragItemLayerId,
    currentDragItemLayerIndex,
    targetLayerId,
    targetLayerIndex,
    isNewLayerAction,
  })

  const normalizeTime = startTime
  const shiftTime = (startTime - currentDragItem.startTime) + dropOffsetX

  let insertedLayersByStartIndex = {}
  let newDragAssetsByLayerIndex = null
  let isAllowedDropAssets = true
  let layersShiftTime = {}

  switch (dropStatus) {
    case DROP_STATUS.CREATE_NEW_LAYER: {
      const data = addNewLayers({
        countDragAssetsLayers,
        insertLayers,
        dispatch,
        layerIndex,
        dragAssetsByLayerIndex,
        getState,
      })
      newDragAssetsByLayerIndex = data.dragAssetsWithNewLayerId
      insertedLayersByStartIndex = data.insertedLayersByStartIndex
    }
      break
    case DROP_STATUS.UPDATE_ASSETS_POSITION_OF_CURRENT_LAYER:
    case DROP_STATUS.CHANGE_LAYER: {
      if (dropStatus === DROP_STATUS.CHANGE_LAYER) {
        const data = changeLayers({
          layers,
          targetLayerIndex,
          currentDragItemLayerIndex,
          dragAssetsByLayerIndex,
          staticAssets,
          insertLayers,
          dispatch,
          Layer,
          shiftTime,
          isIntersectionPlaceholder,
        })
        insertedLayersByStartIndex = data.newLayersByStartIndex
        newDragAssetsByLayerIndex = data.dragAssetsWithNewLayerId
      }

      // get all layers (if new ones have been added)
      const currentLayers = Selectors.getLayers(getState())
      // "updateLayerIndexes" - update layer indexes if a group has been moved up or down
      const draggableAssetsByUpdatedLayerIndex = updateLayerIndexes(
        newDragAssetsByLayerIndex ?? dragAssetsByLayerIndex, currentLayers
      )

      const data = updateAssetsPosition({
        layers,
        dragAssetsByLayerIndex: draggableAssetsByUpdatedLayerIndex,
        staticAssets,
        shiftTime,
        intersectionPlaceholder,
        deltaOnDrop,
      })
      // ShiftTime for static assets after drop draggable assets
      layersShiftTime = data.layersShiftTime
    }
      break
    default:
      isAllowedDropAssets = false
      break
  }

  if (isAllowedDropAssets) {
    await dispatch({
      type: ActionTypes.MULTIPLE_ASSETS_MOVED_ON_TIMELINE,
      payload: {
        dragAssetIds,
        isTransit: false,
        transitionsToMove: patchedDragItem?.dragTransitionIds,
        layerId: targetLayerId,
        shiftTime,
        startTime: normalizeTime,
        startTimeGroup: firstdragAssetStartTime + shiftTime,
        deltaOnDrop,
        dragAssetsByLayerIndex: newDragAssetsByLayerIndex ?? dragAssetsByLayerIndex,
        insertedLayersByStartIndex,
        layersShiftTime,
        layerIds,
        serviceData,
        redoData,
      },
    })
  }
}

export const undoMoveMultipleAssets = ({ serviceData, deltaOnDrop, startTime }) => dispatch => {
  dispatch({
    type: ActionTypes.UNDO_MULTIPLE_MOVE_ASSETS,
    payload: { serviceData, deltaOnDrop, startTime },
  })
}

// ---
// Attaching to another asset, isn't new transition
export const attachTransitionToAsset = (transitionId, leftAssetId, rightAssetId) => (
  (dispatch, getState) => {
    const transition = Selectors.selectAssetById(getState(), transitionId)
    dispatch({
      type: ActionTypes.TRANSITION_ATTACHED_TO_ASSET,
      payload: { transitionId, leftAssetId, rightAssetId, transition },
    })
  }
)

export const addTransitionFromFile = (
  file,
  transitionType,
  leftAssetId,
  rightAssetId,
  options,
  modifiedDuration,
  mountType
) => (dispatch, getState) => {
  const { pregeneratedId, layerId } = options
  const state = getState()
  const creationOrder = Selectors.getLayerAssets(state, layerId).length
  let rightAsset = state.layer.assets.find(asset => asset.id === rightAssetId)
  let leftAsset = state.layer.assets.find(asset => asset.id === leftAssetId)
  let additionAllowed = false
  const currentMountType = mountType ?? file.mountType ?? null

  switch (true) {
    case currentMountType === TRANSITION_MOUNT_TYPE.BETWEEN:
      additionAllowed = isAvailableAttachTransition(leftAsset)
        && isAvailableAttachTransition(rightAsset)
      break
    case currentMountType === TRANSITION_MOUNT_TYPE.IN:
      additionAllowed = isAvailableAttachTransition(rightAsset)
      break
    case currentMountType === TRANSITION_MOUNT_TYPE.OUT:
      additionAllowed = isAvailableAttachTransition(leftAsset)
      break
    default:
      break
  }

  if (additionAllowed) {
    const transitionAsset = file.clone({ // откорректировать длительность транзишена, передав корректровку извне при дроп файла
      leftVideoAssetId: leftAssetId,
      rightVideoAssetId: rightAssetId,
      type: transitionType,
      creationOrder,
      mountType: currentMountType,
      duration: modifiedDuration
        || JSON.parse(
          sessionStorage.getItem(STORAGE_CONST.DEFAULT_TRANSITION_DURATION)
          || JSON.stringify({ [file.type]: file.duration })
        )?.[file.type],
    }, { pregeneratedId })
    dispatch(ActionsLayer.addAsset({ asset: transitionAsset }))
    dispatch({
      type: ActionTypes.TRANSITION_ATTACHED_TO_ASSET,
      payload: {
        transitionId: transitionAsset.id,
        leftAssetId,
        rightAssetId,
        transition: transitionAsset,
      },
    })
    const newState = getState()
    rightAsset = newState.layer.assets.find(asset => asset.id === rightAssetId)
    leftAsset = newState.layer.assets.find(asset => asset.id === leftAssetId)
    const transitionStartTime = getTransitionStartTime(transitionAsset, leftAsset, rightAsset)

    dispatch({
      type: ActionTypes.TRANSITION_SET_START_TIME,
      payload: {
        startTime: transitionStartTime,
        transitionId: transitionAsset.id,
      },
    })
    dispatch(rewind(transitionStartTime))
  }
}

export const reattachTransition = (transitionId, leftAssetId, rightAssetId,
  oldLeftAssetId, oldRightAssetId, mountType, prevMountType) => (dispatch, getState) => {
  dispatch({
    type: ActionTypes.TRANSITION_REATTATCH,
    payload: {
      transitionId,
      leftAssetId,
      rightAssetId,
      oldLeftAssetId,
      oldRightAssetId,
      mountType,
      prevMountType,
    },
  })
  const newState = getState()
  const movedTransition = newState.layer.assets.find(asset => asset.id === transitionId)
  if (movedTransition) {
    dispatch(rewind(movedTransition.startTime))
  }
}

export const createNewTransitionForAsset = ({ id, transitionType, modifiedDuration, mountType },
  leftAssetId, rightAssetId, options = {}) => (dispatch, getState) => {
  const file = Selectors.getSourceFileById(getState(), id)
  dispatch(addTransitionFromFile(
    file, transitionType, leftAssetId, rightAssetId, options, modifiedDuration, mountType
  ))
}

export const loadProjectTransitionAsset = (projectTransitionAsset,
  leftAssetId, rightAssetId, options = {}) => dispatch => {
  const file = parseLoadedFile(projectTransitionAsset.sourceFileType, projectTransitionAsset)
  dispatch(addTransitionFromFile(
    file,
    projectTransitionAsset.type,
    leftAssetId,
    rightAssetId,
    options
  ))
}

export const startDragAsset = assetId => ({
  type: ActionTypes.TIMELINE_DRAG_ASSET,
  payload: assetId,
})

export const endDragAsset = () => ({
  type: ActionTypes.TIMELINE_DRAG_END_ASSET,
})

export const startDragLayer = layerId => ({
  type: ActionTypes.TIMELINE_DRAG_LAYER,
  payload: layerId,
})

export const deleteTransitionAction = (
  transitions,
  relatedAssets,
  assets = [],
  history = false
) => ({ type: ActionTypes.DELETE_TRANSITIONS,
  payload: {
    transitions,
    relatedAssets,
    assets,
    history,
  } })

export const setDnDPatchAssets = assetIds => ({ type: ActionTypes.SET_DND_PATCH_ASSETS,
  payload: { assetIds } })

export const resetDnDPatchAssets = () => ({ type: ActionTypes.RESET_DND_PATCH_ASSETS })

export const endDragLayer = () => ({
  type: ActionTypes.TIMELINE_DRAG_END_LAYER,
})

export const onChangeSliderTime = time => dispatch => {
  dispatch({
    type: ActionTypes.TIMELINE_SET_SLIDER_TIME,
    payload: {
      time,
    },
  })
}


export const clearCopiedAssets = () => ({
  type: ActionTypes.CLEAR_COPIED_ASSETS,
  payload: {},
})


export const showSettings = () => (dispatch, getState) => {
  const selectedAssets = Selectors.getSelectedAssets(getState())

  if (selectedAssets.length === 1) {
    dispatch(openAssetSettingForm(selectedAssets[0].id))
  }
}

export const undoSplittingAssets = () => ({
  type: ActionTypes.UNDO_SPLITTING_ASSETS,
  payload: {},
})

const getPoints = (state, isForward = true) => {
  const allAssets = Selectors.getAssets(state)
  return allAssets.reduce((res, asset) => {
    res.push(asset.startTime, asset.endTime)
    return res
  }, []).sort((a, b) => isForward ? a - b : b - a)
}

export const goClipEditingPoint = (isForward = true) => (dispatch, getState) => {
  const state = getState()
  dispatch(changeTimelinePlaybackState(PLAYBACK_STATE.PAUSE))
  const { sliderTime } = state.timeline
  const points = getPoints(state, isForward)
  const nextPoint = points.find(p => isForward ? p > sliderTime : p < sliderTime) ?? sliderTime
  if (nextPoint !== sliderTime) {
    dispatch(rewind(nextPoint))
  }
}
